// Pure workbook builder: no DOM, no network. Images arrive already encoded,
// so this runs the same in the browser and in Node (tests).

import {
  COLORS,
  COL_PX,
  COL_WIDTHS,
  EMU_PER_PX,
  FONT,
  LAST_COL,
  PAGE_HEIGHT_PT,
  PAGE_SETUP,
  PRODUCTS_PER_PAGE,
  PX_PER_PT,
  RATE_CELL,
  RATE_REF,
  RATE_SHEET,
  ROW_PT,
  SHEET_WIDTH_PX,
} from "./quoteLayout";
import { SMALL_FONT_DELTA, layoutProductRows } from "./quotePageLayout";

const COL_COUNT = COL_WIDTHS.length;
const thin = { style: "thin", color: { argb: COLORS.border } };
const ALL_BORDERS = { top: thin, left: thin, bottom: thin, right: thin };
const font = (extra = {}) => ({ name: FONT, size: 10, ...extra });
const solidFill = (argb) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb },
});
const colLetter = (colIdx) => String.fromCharCode(65 + colIdx); // 0 -> A
// =HYPERLINK() instead of a hyperlink cell: LibreOffice does not wrap
// hyperlink cells (long URLs were cut on paper), formula results wrap fine.
// Excel caps string literals in formulas at 255 chars.
const quoteArg = (s) => `"${String(s).replace(/"/g, '""')}"`;
function linkValue(text, url) {
  if (text.length > 255 || url.length > 255) return { text, hyperlink: url };
  return {
    formula: `HYPERLINK(${quoteArg(url)},${quoteArg(text)})`,
    result: text,
  };
}

const withProtocol = (url) =>
  /^https?:\/\//i.test(url) ? url : "https://" + url;

// Centres an image inside a box (contain), anchored to one cell with EMU
// offsets so the position does not depend on ExcelJS's width estimates.
function placeImage(wb, ws, image, { col, row, boxW, boxH, pad = 3 }) {
  const scale = Math.min(
    (boxW - pad * 2) / image.width,
    (boxH - pad * 2) / image.height
  );
  // floor + clamp: a negative offset is invalid OOXML (LibreOffice drops the
  // picture, Excel offers to "repair" the file)
  const width = Math.floor(image.width * scale);
  const height = Math.floor(image.height * scale);
  const imageId = wb.addImage({
    base64: image.base64,
    extension: image.extension,
  });
  ws.addImage(imageId, {
    tl: {
      nativeCol: col,
      nativeColOff: Math.max(0, Math.round(((boxW - width) / 2) * EMU_PER_PX)),
      nativeRow: row,
      nativeRowOff: Math.max(0, Math.round(((boxH - height) / 2) * EMU_PER_PX)),
    },
    ext: { width, height },
    editAs: "oneCell",
  });
}

// A page that is a single full-width picture (cover / footer).
// Returns the next free row.
function addImagePage(wb, ws, image, startRow) {
  let widthPx = SHEET_WIDTH_PX - 4;
  let heightPt = (widthPx * image.height) / image.width / PX_PER_PT;
  if (heightPt > PAGE_HEIGHT_PT) {
    widthPx = (widthPx * PAGE_HEIGHT_PT) / heightPt;
    heightPt = PAGE_HEIGHT_PT;
  }
  const rowCount = Math.ceil(heightPt / ROW_PT.max);
  for (let i = 0; i < rowCount; i++) {
    ws.getRow(startRow + i).height = heightPt / rowCount;
  }
  placeImage(wb, ws, image, {
    col: 0,
    row: startRow - 1,
    boxW: SHEET_WIDTH_PX,
    boxH: heightPt * PX_PER_PT,
    pad: 0,
  });
  return startRow + rowCount;
}

function labelValue(t, row, size) {
  const parts = [{ text: t.rows[row.label], font: font({ bold: true, size }) }];
  if (row.unit && t.units[row.unit]) {
    parts.push({
      text: " " + t.units[row.unit],
      font: font({ size: size - SMALL_FONT_DELTA }),
    });
  }
  return { richText: parts };
}

function writeCell(cell, spec, ref, row, size) {
  const style = font({ bold: !!(row.highlight || row.bold), size });
  if (spec.hyperlink) {
    cell.value = linkValue(spec.text, spec.hyperlink);
    cell.font = font({
      size: size - SMALL_FONT_DELTA,
      color: { argb: COLORS.link },
      underline: true,
    });
  } else if (spec.formula) {
    const formula = spec.formula(ref);
    const result = spec.result === undefined ? "" : spec.result;
    cell.value = formula ? { formula, result } : result;
    cell.font = style;
  } else {
    cell.value = spec.value === undefined ? "" : spec.value;
    cell.font = style;
  }
  if (spec.numFmt) cell.numFmt = spec.numFmt;
}

function addProductPage(wb, ws, opts, group, groupIdx, startRow) {
  const { t, rows, ctxFor, images } = opts;
  const layout = layoutProductRows({ t, rows, group, groupIdx, ctxFor });
  const size = layout.fontPt;
  let r = startRow;

  // STT header
  const header = ws.getRow(r);
  header.height = ROW_PT.header;
  header.getCell(1).value = t.stt;
  for (let c = 1; c <= PRODUCTS_PER_PAGE; c++) {
    header.getCell(c + 1).value = groupIdx * PRODUCTS_PER_PAGE + c;
  }
  for (let c = 1; c <= COL_COUNT; c++) {
    header.getCell(c).font = font({ bold: true, size });
    header.getCell(c).fill = solidFill(COLORS.header);
  }
  r++;

  // Photos
  const imageRow = ws.getRow(r);
  imageRow.height = ROW_PT.image;
  imageRow.getCell(1).value = t.image;
  imageRow.getCell(1).font = font({ bold: true, size });
  group.forEach((product, idx) => {
    const image = product && images.products.get(product);
    if (!image) return;
    placeImage(wb, ws, image, {
      col: idx + 1,
      row: r - 1,
      boxW: COL_PX[idx + 1],
      boxH: ROW_PT.image * PX_PER_PT,
    });
  });
  r++;

  const rowNumbers = {};
  rows.forEach((row, i) => (rowNumbers[row.key] = r + i));

  rows.forEach((row, i) => {
    const excelRow = ws.getRow(r);
    excelRow.height = layout.heights[i];
    excelRow.getCell(1).value = labelValue(t, row, size);
    group.forEach((product, idx) => {
      if (!product) return;
      const col = colLetter(idx + 1);
      const ref = (key) =>
        rowNumbers[key] ? `${col}${rowNumbers[key]}` : null;
      ref.rate = RATE_REF;
      writeCell(
        excelRow.getCell(idx + 2),
        row.xlsx(ctxFor(product, groupIdx, idx)),
        ref,
        row,
        size
      );
    });
    if (row.highlight) {
      for (let c = 1; c <= COL_COUNT; c++) {
        excelRow.getCell(c).fill = solidFill(COLORS.highlight);
      }
    }
    r++;
  });

  // Borders + alignment for the whole table
  for (let rr = startRow; rr < r; rr++) {
    for (let c = 1; c <= COL_COUNT; c++) {
      const cell = ws.getRow(rr).getCell(c);
      cell.border = ALL_BORDERS;
      cell.alignment = {
        vertical: "middle",
        horizontal: c === 1 ? "left" : "center",
        wrapText: true,
      };
      if (!cell.font) cell.font = font({ size });
    }
  }

  ws.getRow(r).height = ROW_PT.spacer;
  r++;

  addContactRow(wb, ws, r, opts);
  return r + 1;
}

function addContactRow(wb, ws, r, { t, contact, images }) {
  const row = ws.getRow(r);
  row.height = ROW_PT.contact;
  const base = { name: FONT, size: 10 };

  row.getCell(2).value = {
    richText: [
      { text: t.contact + "\n", font: base },
      { text: (contact.name || "") + "\n", font: { ...base, bold: true } },
      { text: t.department, font: base },
    ],
  };
  row.getCell(3).value = {
    richText: [
      { text: "☎ ", font: base },
      {
        text: contact.phone || "",
        font: { ...base, bold: true, color: { argb: COLORS.red } },
      },
    ],
  };
  ws.mergeCells(r, 4, r, 5);
  row.getCell(4).value = {
    richText: [{ text: "✉ " + (contact.email || ""), font: base }],
  };
  if (contact.website) {
    row.getCell(6).value = linkValue(
      contact.website,
      withProtocol(contact.website)
    );
    row.getCell(6).font = { ...base, color: { argb: COLORS.link } };
  }

  for (let c = 1; c <= COL_COUNT; c++) {
    const cell = row.getCell(c);
    cell.alignment = {
      vertical: "middle",
      horizontal: c === 2 ? "left" : "center",
      wrapText: true,
    };
    cell.border =
      c === 1
        ? ALL_BORDERS
        : {
            top: thin,
            bottom: thin,
            left: c === 2 ? thin : undefined,
            right: c === COL_COUNT ? thin : undefined,
          };
  }

  if (images.logo) {
    placeImage(wb, ws, images.logo, {
      col: 0,
      row: r - 1,
      boxW: COL_PX[0],
      boxH: ROW_PT.contact * PX_PER_PT,
      pad: 5,
    });
  }
}

/**
 * @param {object} ExcelJS  the exceljs module
 * @param {object} opts
 * @param {object} opts.t          messages of the chosen language
 * @param {Array}  opts.groups     arrays of PRODUCTS_PER_PAGE (null = empty slot)
 * @param {Array}  opts.rows       visible QUOTE_ROWS
 * @param {Function} opts.ctxFor   (product, groupIdx, idx) => row ctx
 * @param {object} opts.contact    { name, phone, email, website }
 * @param {number} opts.rate       USD -> VND
 * @param {object} opts.images     { cover, footer, logo, products: Map }
 * @returns {{ workbook, worksheet, pages: Array<{type, firstRow, lastRow}> }}
 */
export function buildQuoteWorkbook(ExcelJS, opts) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "Galaxy Office";
  wb.created = new Date();
  const ws = wb.addWorksheet(opts.t.sheetName, {
    views: [{ showGridLines: false }],
    pageSetup: { ...PAGE_SETUP },
  });
  ws.columns = COL_WIDTHS.map((width) => ({ width }));

  // hidden sheets are never printed; formulas reference RATE_REF
  const rateSheet = wb.addWorksheet(RATE_SHEET, { state: "hidden" });
  rateSheet.getCell("A1").value = "USD/VND";
  rateSheet.getCell(RATE_CELL).value = Number(opts.rate) || 0;

  const pages = [];
  const addPage = (type, firstRow, nextRow, pageBreak) => {
    pages.push({ type, firstRow, lastRow: nextRow - 1 });
    if (pageBreak) ws.getRow(nextRow - 1).addPageBreak();
    return nextRow;
  };

  let r = 1;
  r = addPage("cover", r, addImagePage(wb, ws, opts.images.cover, r), true);
  opts.groups.forEach((group, groupIdx) => {
    r = addPage(
      "products",
      r,
      addProductPage(wb, ws, opts, group, groupIdx, r),
      true
    );
  });
  r = addPage("footer", r, addImagePage(wb, ws, opts.images.footer, r), false);

  // ExcelJS only prefixes the column with "$": "A$1" -> "$A$1" (absolute,
  // the form Excel itself writes)
  ws.pageSetup.printArea = `A$1:${LAST_COL}$${r - 1}`;
  return { workbook: wb, worksheet: ws, pages };
}
